import React from "react";

/**
 * AttachmentViewer - Displays attachments as small cards
 * props:
 *  - attachments: [{ filename, content_type, data (base64), size }]
 *  - onAttachmentClick: function(attachment) - called when an attachment is clicked
 */
export default function AttachmentViewer({ attachments = [], onAttachmentClick }) {
  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-muted small p-3 text-center">
        No attachments available
      </div>
    );
  }

  const getFileIcon = (contentType, filename) => {
    if (contentType?.startsWith("image/")) {
      return <i className="fas fa-image" style={{ fontSize: "20px" }} />;
    } else if (contentType === "application/pdf" || filename?.toLowerCase().endsWith(".pdf")) {
      return <i className="fas fa-file-pdf" style={{ fontSize: "20px", color: "#dc3545" }} />;
    } else {
      return <i className="fas fa-file" style={{ fontSize: "20px" }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Helper to check if attachment is an image (by mimeType or file extension)
  const isImageFile = (att) => {
    const contentType = att.content_type || att.mimeType || "";
    const filename = (att.filename || "").toLowerCase();
    
    // Check mimeType first
    if (contentType.startsWith("image/")) {
      return true;
    }
    
    // Fallback: check file extension for common image formats
    const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"];
    return imageExtensions.some(ext => filename.endsWith(ext));
  };

  return (
    <div style={{ padding: "12px", maxHeight: "400px", overflowY: "auto" }}>
      <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
        {attachments.map((att, idx) => {
          const isImage = isImageFile(att);
          // Use web_path URL if available, otherwise fall back to base64 data
          const imageUrl = att.web_path 
            ? `https://shipreportstvm.cloud/api/${att.web_path}` 
            : (att.data ? `data:${att.content_type || att.mimeType || "image/png"};base64,${att.data}` : null);

          return (
            <div
              key={idx}
              onClick={() => onAttachmentClick && onAttachmentClick(att, idx)}
              style={{
                width: "120px",
                height: "140px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "8px",
                cursor: "pointer",
                background: "#fff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                transition: "all 0.2s",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {isImage && imageUrl ? (
                <img
                  src={imageUrl}
                  alt={att.filename}
                  style={{
                    width: "100%",
                    height: "80px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  {getFileIcon(att.content_type, att.filename)}
                </div>
              )}
              <div
                style={{
                  marginTop: "6px",
                  fontSize: "10px",
                  textAlign: "center",
                  fontWeight: "500",
                  color: "#333",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={att.filename}
              >
                {att.filename}
              </div>
              <div
                style={{
                  fontSize: "9px",
                  color: "#666",
                  marginTop: "2px",
                }}
              >
                {formatFileSize(att.size)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

